import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { dashboardAPI, fileAPI, usersAPI } from '../services/api';
import { DashboardData, User } from '../types';
import Modal from '../components/Modal';
import FileViewer from '../components/FileViewer';
import StatusBadge from '../components/StatusBadge';
import { isImageFile, getFileIcon, formatFileSize, API_BASE } from '../utils/fileUtils';
import { useFileViewer } from '../hooks/useFileViewer';
import '../styles/global.css';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [cities, setCities] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedNotification, setSelectedNotification] = useState<any>(null);
  const fileViewer = useFileViewer();

  useEffect(() => {
    loadCities();
    initDashboard();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const initDashboard = async () => {
    let city = '';
    try {
      const userData: User = await usersAPI.getCurrentUser();
      if (userData.city) {
        city = userData.city;
        setSelectedCity(city);
      }
    } catch (err) {
      console.error('Failed to load current user city:', err);
    }
    await loadDashboardData(city);
  };

  const loadCities = async () => {
    try {
      const citiesData = await dashboardAPI.getCities();
      setCities(citiesData);
    } catch (err) {
      console.error('Failed to load cities:', err);
    }
  };

  const loadDashboardData = async (city: string = selectedCity) => {
    try {
      if (!dashboardData) setLoading(true);
      const data = await dashboardAPI.getDashboardData(city || undefined);
      setDashboardData(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const getUserProgress = (achievementId: number) => {
    // Для простоты используем данные из dashboardData.achievements
    // В реальном приложении здесь может быть отдельный запрос к API
    const achievement = dashboardData?.achievements.find(a => a.id === achievementId);
    return achievement || { percentage_completed: 0, is_completed: false };
  };

  const getNotificationAction = (type: string): { label: string; path: string } | null => {
    switch (type) {
      case 'witness_request':
      case 'witness_confirmed':
      case 'witness_rejected':
      case 'task_review_ready':
        return { label: 'Перейти к заданиям', path: '/tasks' };
      case 'profile':
        return { label: 'Открыть профиль', path: '/profile' };
      default:
        return null;
    }
  };

  const handleNotificationClick = (notification: any) => {
    setSelectedNotification(notification);
  };

  const closeNotificationModal = () => {
    setSelectedNotification(null);
  };


    if (loading) {
    return <div className="loading">Загрузка...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (!dashboardData) {
    return <div className="error">Нет данных для отображения</div>;
  }

  return (
    <div className="container">
      <div className="dashboard-content">
        <div className="dashboard-main">
          {/* Статистика прогресса */}
          <div className="dashboard-stats">
            <div className="stat-card">
              <div className="stat-icon">
                <div className="icon-chart"></div>
              </div>
              <div className="stat-content">
                <h3>{dashboardData.achievements.filter(a => getUserProgress(a.id).is_completed).length}</h3>
                <p>Завершено достижений</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">
                <div className="icon-task"></div>
              </div>
              <div className="stat-content">
                <h3>{dashboardData.tasks.filter(t => t.status === 'approved').length}</h3>
                <p>Выполнено заданий</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">
                <div className="icon-time"></div>
              </div>
              <div className="stat-content">
                <h3>{dashboardData.tasks.filter(t => t.status === 'pending' || t.status === 'submitted').length}</h3>
                <p>Активных заданий</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">
                <div className="icon-target"></div>
              </div>
              <div className="stat-content">
                <h3>{
                  dashboardData.achievements.length > 0
                    ? Math.round(dashboardData.achievements.reduce((acc, a) => acc + getUserProgress(a.id).percentage_completed, 0) / dashboardData.achievements.length)
                    : 0
                }%</h3>
                <p>Общий прогресс</p>
              </div>
            </div>
          </div>

          {/* Мои достижения */}
          <div className="dashboard-section">
            <div className="section-header">
              <h2>Мои достижения</h2>
              <Link to="/achievements" className="view-all-button">Все достижения</Link>
            </div>
            <div className="achievements-grid">
              {dashboardData.achievements.slice(0, 6).map(achievement => {
                const progress = getUserProgress(achievement.id);
                return (
                  <div key={achievement.id} className="achievement-card">
                    <div className="achievement-icon">
                      <div className="default-achievement-icon">Д</div>
                    </div>
                    <div className="achievement-info">
                      <h4>{achievement.title}</h4>
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{
                            width: `${progress.percentage_completed}%`,
                            backgroundColor: progress.is_completed ? 'var(--success)' : 'var(--primary-orange)'
                          }}
                        />
                      </div>
                      <div className="progress-text">
                        {progress.percentage_completed.toFixed(1)}% • {progress.is_completed ? 'Завершено' : 'В процессе'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Текущие задания */}
          <div className="dashboard-section">
            <div className="section-header">
              <h2>Текущие задания</h2>
              <Link to="/tasks" className="view-all-button">Все задания</Link>
            </div>
            <div className="tasks-list">
              {dashboardData.tasks.slice(0, 5).map(task => (
                <div key={task.id} className="task-item">
                  <div className="task-status">
                    <span className={`status-badge status-${task.status}`}>
                      {task.status === 'pending' ? 'Ожидает' :
                       task.status === 'submitted' ? 'Отправлено' :
                       task.status === 'approved' ? 'Одобрено' : 'Отклонено'}
                    </span>
                  </div>
                  <div className="task-content">
                    <h4>{task.title}</h4>
                    {task.submitted_at && (
                      <p className="task-date">
                        {new Date(task.submitted_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Боковая панель */}
        <div className="dashboard-sidebar">
          {/* Уведомления */}
          <div className="sidebar-section">
            <h3>Уведомления</h3>
            <div className="notifications-list">
              {dashboardData.notifications.slice(0, 5).map(notification => (
                <div
                  key={notification.id}
                  className="notification-item clickable"
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-type">
                    <div className={`notification-icon ${
                      notification.notification_type === 'witness_request' ? 'icon-profile' :
                      notification.notification_type === 'profile' ? 'icon-profile' : 'icon-info'
                    }`}></div>
                  </div>
                  <div className="notification-content">
                    <h4>{notification.title}</h4>
                    {notification.notification_type === 'witness_request' && (
                      <span style={{ fontSize: '11px', color: 'var(--primary-orange)', fontWeight: 600 }}>
                        👥 Требует ответа
                      </span>
                    )}
                    <span className="notification-date">
                      {new Date(notification.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Фильтр по городам */}
          <div className="sidebar-section">
            <h3>Фильтр по городу</h3>
            <select
              className="form-control"
              value={selectedCity}
              onChange={(e) => { setSelectedCity(e.target.value); loadDashboardData(e.target.value); }}
            >
              <option value="">Все города</option>
              {cities.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </div>

          {/* Команда */}
          <div className="sidebar-section">
            <h3>Команда</h3>
            <div className="team-list">
              {dashboardData.users.slice(0, 8).map(user => (
                <div key={user.id} className="team-member">
                  <div className="member-avatar">
                    {user.avatar_path ? (
                      <img
                        src={`${API_BASE}/uploads/${user.avatar_path}`}
                        alt={user.full_name}
                      />
                    ) : (
                      <div className="avatar-placeholder">
                        {user.full_name.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="member-info">
                    <h4>{user.full_name}</h4>
                    <p>@{user.username} {user.city && `• ${user.city}`}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Модальное окно уведомления */}
      <Modal isOpen={!!selectedNotification} onClose={closeNotificationModal} maxWidth="700px">
        {selectedNotification && (() => {
          const action = getNotificationAction(selectedNotification.notification_type);
          return (
          <>
            <div className="notification-modal-header">
              <div className="notification-modal-title-row">
                <h2 className="modal-title">{selectedNotification.title}</h2>
              </div>
              <p className="notification-modal-date">
                {new Date(selectedNotification.created_at).toLocaleDateString('ru-RU', {
                  year:'numeric', month:'long', day:'numeric', hour:'2-digit', minute:'2-digit'
                })}
              </p>
            </div>

            <div className="notification-modal-message">
              <p>{selectedNotification.message}</p>
            </div>

            {action && (
              <div style={{ marginTop: '1.5rem' }}>
                <button
                  className="btn btn-primary"
                  onClick={() => { closeNotificationModal(); navigate(action.path); }}
                >
                  {action.label} →
                </button>
              </div>
            )}

            {selectedNotification.files && selectedNotification.files.length > 0 && (
              <div>
                <h3 className="notification-modal-files-title">
                  Прикреплённые файлы ({selectedNotification.files.length})
                </h3>
                <div className="notification-files-grid">
                  {selectedNotification.files.map((file: any, index: number) => {
                    const filename = file.file_path.split('/').pop();
                    const isImg = isImageFile(file.filename);
                    return (
                      <div key={index} className="notification-file-card">
                        <div
                          className="notification-file-preview"
                          onClick={() => {
                            const names = selectedNotification.files.map((f: any) => f.file_path.split('/').pop());
                            fileViewer.open(names, index);
                          }}
                        >
                          {isImg ? (
                            <img src={`${API_BASE}/uploads/notifications/${filename}`} alt={file.filename} />
                          ) : (
                            <span className="notification-file-preview-icon">{getFileIcon(file.filename)}</span>
                          )}
                        </div>
                        <div className="notification-file-info">
                          <p className="notification-file-name" title={file.filename}>{file.filename}</p>
                          <p className="notification-file-meta">{formatFileSize(file.file_size)} • {file.content_type || '—'}</p>
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => fileAPI.downloadFile(`notifications/${filename}`)}
                          >
                            Скачать
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
          );
        })()}
      </Modal>

      {/* Просмотрщик файлов */}
      {fileViewer.isOpen && (
        <FileViewer
          files={fileViewer.files}
          currentIndex={fileViewer.currentIndex}
          folder="notifications"
          onClose={fileViewer.close}
          onNext={fileViewer.next}
          onPrev={fileViewer.prev}
          onDownload={(filename) => fileAPI.downloadFile(`notifications/${filename}`)}
        />
      )}
    </div>
  );
};

export default Dashboard;
